import type { CSSProperties, ReactNode } from "react";

import { cn } from "~/lib/utils";

/**
 * A miniature of the app — rail, a short conversation, the composer.
 *
 * Previewing a theme as a swatch strip tells you its colours; previewing it as
 * the app tells you what it will feel like to sit in, which is the question
 * someone opening this screen is actually asking. The proportions matter more
 * than the detail: the rail is the widest block of colour a theme controls, so
 * a preview that omits it misrepresents every palette with a tinted rail.
 */
export interface WireframeColors {
  readonly canvas: string;
  readonly sidebar: string;
  readonly card: string;
  readonly accent: string;
  readonly primary: string;
  readonly border: string;
}

/** Hairlines are drawn at a fixed neutral so a pane reads at any lightness. */
const HAIRLINE = "rgb(127 127 127 / 0.25)";

function Pane(props: {
  readonly colors: WireframeColors;
  readonly clip?: "left" | "right" | undefined;
}): ReactNode {
  const { colors, clip } = props;
  const clipStyle: CSSProperties | undefined =
    clip === undefined
      ? undefined
      : {
          clipPath:
            clip === "left"
              ? "polygon(0 0, calc(50% - 0.5px) 0, calc(50% - 0.5px) 100%, 0 100%)"
              : "polygon(calc(50% + 0.5px) 0, 100% 0, 100% 100%, calc(50% + 0.5px) 100%)",
        };

  return (
    <span className="absolute inset-0" style={clipStyle}>
      <span className="absolute inset-0" style={{ backgroundColor: colors.canvas }} />
      <span
        className="absolute inset-y-0 left-0 w-[22%]"
        style={{ backgroundColor: colors.sidebar, boxShadow: `inset -1px 0 0 ${HAIRLINE}` }}
      />
      {/* Rail: a search field, then thread rows with the active one tinted. */}
      <span
        className="absolute top-[9%] left-[3%] h-[8%] w-[16%] rounded-[2px]"
        style={{ backgroundColor: colors.card, boxShadow: `inset 0 0 0 1px ${HAIRLINE}` }}
      />
      <span
        className="absolute top-[24%] left-[3%] h-[7%] w-[16%] rounded-[2px]"
        style={{ backgroundColor: colors.accent }}
      />
      <span
        className="absolute top-[35%] left-[3%] h-[7%] w-[16%] rounded-[2px] opacity-60"
        style={{ backgroundColor: colors.accent }}
      />
      {/* Conversation: one bubble and two lines of reply. */}
      <span
        className="absolute top-[12%] right-[6%] h-[10%] w-[26%] rounded-[3px]"
        style={{ backgroundColor: colors.card, boxShadow: `inset 0 0 0 1px ${HAIRLINE}` }}
      />
      <span
        className="absolute top-[30%] left-[27%] h-[4%] w-[34%] rounded-[2px]"
        style={{ backgroundColor: HAIRLINE }}
      />
      <span
        className="absolute top-[39%] left-[27%] h-[4%] w-[26%] rounded-[2px]"
        style={{ backgroundColor: HAIRLINE }}
      />
      {/* Composer, with the send control in the theme's primary. */}
      <span
        className="absolute right-[6%] bottom-[9%] left-[27%] h-[16%] rounded-[3px]"
        style={{ backgroundColor: colors.card, boxShadow: `inset 0 0 0 1px ${colors.border}` }}
      />
      <span
        className="absolute right-[9%] bottom-[13%] h-[8%] w-[9%] rounded-[2px]"
        style={{ backgroundColor: colors.primary }}
      />
    </span>
  );
}

/**
 * `panes` takes two entries for the system option, which shows light and dark
 * split down the middle — the only honest preview of "follow the system", since
 * the answer depends on a setting this app does not own.
 */
export function ThemeWireframe(props: {
  readonly panes: readonly {
    readonly colors: WireframeColors;
    readonly clip?: "left" | "right" | undefined;
  }[];
  readonly className?: string;
}): ReactNode {
  return (
    <span
      aria-hidden
      className={cn(
        // Fixed height, not an aspect ratio. The reference pins these at
        // 8.75rem so every preview is the same size whatever column count the
        // grid settles on — an aspect ratio would make a tile in a two-up row
        // noticeably taller than the same tile in a four-up row, and the
        // wireframes would stop being comparable at a glance.
        // Below `sm` the preview keeps the app's own proportions; from `sm`
        // up it takes the reference's fixed 8.75rem so every tile in the grid
        // is the same height whatever column count auto-fit settles on.
        //
        // A fixed height alone was wrong on a phone: the colour-scheme row is
        // three columns at every width, so at 390px the tile is ~100px wide
        // and the miniature rendered 82x140 — a portrait picture of a
        // landscape application.
        "relative block aspect-[16/10] w-full overflow-hidden rounded-md border border-border sm:aspect-auto sm:h-[8.75rem]",
        props.className,
      )}
    >
      {props.panes.map((pane, index) => (
        <Pane
          clip={pane.clip}
          colors={pane.colors}
          key={`${pane.clip ?? "full"}-${String(index)}`}
        />
      ))}
    </span>
  );
}
